import React, { useCallback, useEffect, useMemo } from 'react'
import { Col, Divider, Form, Row } from 'antd'
import { filter, map, some } from 'lodash'
import PropTypes from 'prop-types'

import { areSameValue, getFilterOptions } from '../../../core/utils'

import { FormButtons, FormSelect } from '../../../common/components/sections'
import {
  StyledAiHighlightedTag,
  StyledPaddingTopCol,
} from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import { useFormDirty } from '../../../common/hooks'
import { treeOptionPropType } from '../../../common/prop-types'

// import { useUserHasPermissions } from '../../../account/hooks'
import { CURATION_DETAIL_LEVEL } from '../../constants'
import { menuCurationTaskPropType } from '../../prop-types'

import { menuCurationTaskDetailLevelAndLanguageFormInitialValues } from './initial-values'

const MenuCurationTaskDetailLevelAndLanguageForm = ({
  menuCurationTask,
  languageOptions,
  isLoading,
  isDisabled,
  onSubmit,
}) => {
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)
  const initialValues = useMemo(
    () =>
      menuCurationTaskDetailLevelAndLanguageFormInitialValues(menuCurationTask),
    [menuCurationTask],
  )

  // const hasPermissionToChangeDetailLevel = useUserHasPermissions(
  //   PERMISSIONS.MENU_CURATION_TASK.UPDATE_DETAIL_LEVEL,
  // )

  const curationDetailLevelOptions = useMemo(
    () => getFilterOptions(CURATION_DETAIL_LEVEL, 'label', 'value'),
    [],
  )

  // This is temporarily disabled until curators get more experience with the tool
  // const areDetailLevelSelectsDisabled = useMemo(
  //   () => isDisabled || !hasPermissionToChangeDetailLevel,
  //   [hasPermissionToChangeDetailLevel, isDisabled],
  // )

  const highlightedLanguageIds = useMemo(
    () =>
      map(
        filter(menuCurationTask.languages, 'isSuggestedByAi'),
        ({ language }) => language.id,
      ),
    [menuCurationTask.languages],
  )

  const handleTagRender = useCallback(
    tag => {
      const { onClose, label, value: tagValue } = tag
      const isSuggestedByAi = some(highlightedLanguageIds, highlightedId =>
        areSameValue(highlightedId, tagValue),
      )

      return (
        <StyledAiHighlightedTag
          onClose={onClose}
          closable
          isSuggestedByAi={isSuggestedByAi}
        >
          {label}
        </StyledAiHighlightedTag>
      )
    },
    [highlightedLanguageIds],
  )

  useEffect(() => {
    resetForm()
  }, [initialValues, resetForm])

  return (
    <Form
      form={form}
      initialValues={initialValues}
      name="editMenuCurationTaskLanguages"
      onFinish={onSubmit}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <Row gutter={[16]}>
        <Col span={8}>
          <FormSelect
            allowClear
            name="languageIds"
            label="Languages"
            labelAbove
            placeholder="Select Languages..."
            options={languageOptions}
            mode="multiple"
            disabled={isDisabled}
            tagRender={handleTagRender}
          />
        </Col>
        <Col span={4}>
          <FormSelect
            label="Curation detail level"
            labelAbove
            name="curationDetailLevel"
            options={curationDetailLevelOptions}
            disabled
            placeholder="Select curation detail level."
          />
        </Col>
        <StyledPaddingTopCol span={6}>
          <FormButtons
            permissions={[
              PERMISSIONS.MENU_CURATION_TASK.UPDATE_DETAIL_LEVEL,
              PERMISSIONS.MENU_CURATION_TASK.UPDATE,
            ]}
            isDisabled={!isDirty}
            isLoading={isLoading}
            onReset={resetForm}
          />
        </StyledPaddingTopCol>
      </Row>
      <Divider />
    </Form>
  )
}

MenuCurationTaskDetailLevelAndLanguageForm.propTypes = {
  menuCurationTask: menuCurationTaskPropType.isRequired,
  onSubmit: PropTypes.func.isRequired,
  languageOptions: PropTypes.arrayOf(treeOptionPropType),
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
}

MenuCurationTaskDetailLevelAndLanguageForm.defaultProps = {
  isLoading: false,
  isDisabled: false,
  languageOptions: [],
}

export default MenuCurationTaskDetailLevelAndLanguageForm
